import React, { useState } from 'react';
import { analyzeAndSanitize, hasEmbeds, extractEmbedInfo } from '../../utils/sanitizeHtml';

function TestEmbedPage() {
  const [inputHtml, setInputHtml] = useState('');
  const [sanitizedResult, setSanitizedResult] = useState(null);
  
  // Sample Bandcamp embed for testing
  const sampleBandcampEmbed = `
<p>Here's some text before the embed.</p>
<div class="raw-html-embed">
<iframe style="border: 0; width: 100%; height: 120px;" 
  src="https://bandcamp.com/EmbeddedPlayer/album=363925150/size=large/bgcol=ffffff/linkcol=0687f5/tracklist=false/artwork=small/transparent=true/" 
  seamless>
  <a href="https://whitelabrecs.bandcamp.com/album/chasyng-drakens">Chasyng Drakens by Ozbolt</a>
</iframe>
</div>
<p>And some text after the embed.</p>
`;

  // Sample YouTube embed
  const sampleYouTubeEmbed = `
<p>YouTube video:</p>
<div class="raw-html-embed">
<iframe width="560" height="315" 
  src="https://www.youtube.com/embed/dQw4w9WgXcQ" 
  title="YouTube video player" 
  frameborder="0" 
  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
  allowfullscreen>
</iframe>
</div>
`;

  // Sample with untrusted domain
  const sampleUntrustedEmbed = `
<p>Untrusted embed:</p>
<div class="raw-html-embed">
<iframe src="https://evil-site.com/embed"></iframe>
</div>
<p>This should be stripped.</p>
`;

  const testSanitization = () => {
    console.clear();
    console.log('Testing sanitization with input:', inputHtml);
    
    const hasEmbedsResult = hasEmbeds(inputHtml);
    const embedInfo = extractEmbedInfo(inputHtml);
    const result = analyzeAndSanitize(inputHtml);
    
    setSanitizedResult({
      hasEmbeds: hasEmbedsResult,
      embedInfo: embedInfo,
      ...result
    });
  };

  const loadSample = (sample) => {
    setInputHtml(sample);
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1>Embed Sanitization Test Page</h1>
      
      <div style={{ marginBottom: '20px' }}>
        <h2>Quick Load Samples:</h2>
        <button onClick={() => loadSample(sampleBandcampEmbed)} style={{ marginRight: '10px' }}>
          Load Bandcamp Embed
        </button>
        <button onClick={() => loadSample(sampleYouTubeEmbed)} style={{ marginRight: '10px' }}>
          Load YouTube Embed
        </button>
        <button onClick={() => loadSample(sampleUntrustedEmbed)} style={{ marginRight: '10px' }}>
          Load Untrusted Embed
        </button>
        <button onClick={() => setInputHtml('')}>
          Clear
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        <div>
          <h2>Input HTML:</h2>
          <textarea
            value={inputHtml}
            onChange={(e) => setInputHtml(e.target.value)}
            style={{ 
              width: '100%', 
              height: '300px', 
              fontFamily: 'monospace',
              fontSize: '12px'
            }}
            placeholder="Paste your HTML with embeds here..."
          />
          <button 
            onClick={testSanitization}
            style={{ 
              marginTop: '10px',
              padding: '10px 20px',
              background: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Test Sanitization
          </button>
        </div>

        <div>
          <h2>Sanitization Result:</h2>
          {sanitizedResult && (
            <div>
              <div style={{ 
                background: '#f0f0f0', 
                padding: '10px', 
                borderRadius: '4px',
                marginBottom: '10px'
              }}>
                <h3>Analysis:</h3>
                <p><strong>Has Embeds:</strong> {sanitizedResult.hasEmbeds ? 'Yes' : 'No'}</p>
                <p><strong>Embed Info:</strong></p>
                <pre style={{ fontSize: '10px', overflow: 'auto' }}>
                  {JSON.stringify(sanitizedResult.embedInfo, null, 2)}
                </pre>
                <p><strong>Report:</strong></p>
                <pre style={{ fontSize: '10px', overflow: 'auto' }}>
                  {JSON.stringify(sanitizedResult.report, null, 2)}
                </pre>
              </div>

              <div style={{ 
                background: '#fff', 
                padding: '10px', 
                border: '1px solid #ddd',
                borderRadius: '4px',
                marginBottom: '10px'
              }}>
                <h3>Sanitized HTML (Raw):</h3>
                <textarea
                  value={sanitizedResult.html}
                  readOnly
                  style={{ 
                    width: '100%', 
                    height: '200px', 
                    fontFamily: 'monospace',
                    fontSize: '10px'
                  }}
                />
              </div>

              <div style={{ 
                background: '#fff', 
                padding: '10px', 
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}>
                <h3>Rendered Output:</h3>
                <div 
                  dangerouslySetInnerHTML={{ __html: sanitizedResult.html }}
                  style={{ 
                    padding: '10px',
                    background: '#fafafa'
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={{ marginTop: '40px', padding: '20px', background: '#f9f9f9', borderRadius: '4px' }}>
        <h2>Console Output:</h2>
        <p>Open your browser's Developer Console to see detailed sanitization logs.</p>
        <p>The sanitization function logs:</p>
        <ul>
          <li>Original content analysis</li>
          <li>Found embeds and iframes</li>
          <li>Trust validation for each domain</li>
          <li>Placeholder replacement process</li>
          <li>Final sanitization results</li>
        </ul>
      </div>
    </div>
  );
}

export default TestEmbedPage;
